import { Roles } from '@omnea-digital/toolbox'
import { useQuery } from '@tanstack/react-query'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { Renewal } from '@omnea-app/renewals/renewalsFrontendClient'
import { RenewalsHttpClient } from '@omnea-app/renewals/renewalsHttpClient'
import { renewalKeys } from './query-key-factory'

const renewalsRecordsPerBatch = 100

const fetchRenewalsBatch = async (apiClient: RenewalsHttpClient, page: number) => {
  const { data } = await apiClient.POST('/paginated', {
    body: { paginationModel: { page, pageSize: renewalsRecordsPerBatch } },
  })
  return data
}

export const useRenewalsList = () => {
  const { user } = useAuthenticatedUser()
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    enabled: user.role === Roles.ADMIN || user.role === Roles.USER,
    queryKey: renewalKeys.list,
    queryFn: async () => {
      const authorization = await getToken()
      const renewalsApiClient = new RenewalsHttpClient({ authorization })

      const firstBatch = await fetchRenewalsBatch(renewalsApiClient, 0)
      if (!firstBatch) return []
      const totalPages = Math.ceil(firstBatch.totalCount / renewalsRecordsPerBatch)
      if (totalPages < 2) {
        return firstBatch.renewals as Renewal[]
      }

      // All pages starting from index 1 (page 2)
      const pagesToLoad = range(1, totalPages)
      const restOfBatches = await Promise.all(pagesToLoad.map((page) => fetchRenewalsBatch(renewalsApiClient, page)))
      const allRenewals = [...firstBatch.renewals, ...restOfBatches.map((data) => data?.renewals).flat()].filter(
        Boolean
      ) as Renewal[]

      // remove duplicates between batches just in case
      return uniqBy(allRenewals, 'id')
    },
  })
}
